import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '@adornis/buchungsheld-db/server/scoped-collection.js';
import { context } from '@adornis/baseql/server/context.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { IcsrmUniEnum } from '@adornis/bh-plugin-impl/stammdaten/icsrm-stammdaten-plugin.js';
import { AdornisFileAdapter } from '@adornis/file-extractor/adapters/file-input/AdornisFileAdapter.js';
import { OcrAdapter } from '@adornis/file-extractor/adapters/file-type/OcrAdapter.js';
import { FileExtractionOrchestrator } from '@adornis/file-extractor/file-extraction-orchestrator.js';
import { AdornisLLMRouter } from '@adornis/llm/server/llm-router.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import { ResumeFile } from '../db/ResumeFile.js';
import {
  EmploymentType,
  ExpectedAcademicTitle,
  PreferredStudyField,
  SoftSkills,
  SpeechLevel,
} from '../db/job-position.js';
import {
  MAX_SOFT_SKILLS,
  MWUserData,
  OtherSpeechLevels,
  PracticalExperience,
  PracticalExperienceLevel,
} from '../db/user.js';
import { azureFoundryLLM } from './llm.js';
import { getAndEnsureUserDataOfUserID } from './user.js';
import { getResumeFileByID } from './resume-file.js';
import { parseDateOnly } from '../shared/parse-date-only.js';

export const extractCvInformation = registerMutation({
  operationName: 'extractCvInformation',
  type: () => String,
  resolve: (fileID: string) => async () => {
    // check if called on client and user is logged in
    if (context.serverContext) throw new Error('extractCvInformation can only be called on the client');
    if (!context.userID) throw new NotLoggedInError();
    const userData = await getAndEnsureUserDataOfUserID(context.userID)(selectionSet(() => MWUserData, 2));

    if (!userData) throw new Error('User not found');

    // The upload route creates ResumeFile in the caller's session and binds ownerID in beforeCreate.
    // Do not accept an arbitrary file ID supplied by another logged-in user.
    const file = await getResumeFileByID(fileID)({ _id: 1, ownerID: 1 });
    if (!file) throw new Error('File not found');
    if (file.ownerID !== context.userID) throw new Error('File does not belong to the logged-in user');

    userData.resumeFileId = fileID;

    // get file content
    const orchestrator = new FileExtractionOrchestrator([new OcrAdapter()]);

    logger.info({ message: 'Extracting CV data', fileID: userData.resumeFileId, userID: context.userID });

    let content: any;
    try {
      content = await orchestrator.extractData({
        llmRouter: new AdornisLLMRouter(azureFoundryLLM('gpt-5.6-luna')),
        inputData: [new AdornisFileAdapter({ fileID: userData.resumeFileId, fileClass: ResumeFile })],
        instruction: `Gebe die Antworten in der angegebenen Sprache zurück. Es darf nicht einfach übersetzt werden.
<feld-definitionen>
  <feld name="university" type="string">Needs to be EXACTLY one of: ${Object.entries(IcsrmUniEnum)
    .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
    .map(([key, value]) => value)
    .join(',')}</feld>
  <feld name="department" type="string">Needs to be EXACTLY one of: ${Object.entries(PreferredStudyField)
    .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
    .map(([key, value]) => value)
    .join(',')}</feld>
  <feld name="languageSkillsDE" type="string">Language level in German. Needs to be EXACTLY one of: ${Object.entries(
    SpeechLevel,
  )
    .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
    .map(([key, value]) => value)
    .join(',')}</feld>
  <feld name="languageSkillsEN" type="string">Language level in English. Needs to be EXACTLY one of: ${Object.entries(
    SpeechLevel,
  )
    .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
    .map(([key, value]) => value)
    .join(',')}</feld>
  <feld name="otherLanguages" type="array">
    <feld name="language" type="string"></feld>
    <feld name="level" type="string">Language level in a given language. Needs to be EXACTLY one of: ${Object.entries(
      SpeechLevel,
    )
      .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
      .map(([key, value]) => value)
      .join(',')}</feld>
  </feld>
  <feld name="techSkills" type="string"></feld>
  <feld name="softSkills" type="array">
    <feld name="skill" type="string">Needs to be EXACTLY one of: ${Object.entries(SoftSkills)
      .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
      .map(([key, value]) => value)
      .join(',')}</feld>
  </feld>
  <feld name="practicalExperiences" type="array">
    <feld name="description" type="string"></feld>
    <feld name="level" type="string">Needs to be EXACTLY one of: ${Object.entries(PracticalExperienceLevel)
      .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
      .map(([key, value]) => value)
      .join(',')}</feld>
  </feld>
  <feld name="desiredEmploymentTypes" type="array">
    <feld name="employmentType" type="string">Needs to be EXACTLY one of: ${Object.entries(EmploymentType)
      .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
      .map(([key, value]) => value)
      .join(',')}</feld>
  </feld>
</feld-definitionen>

Beachte die <feld-definitionen> bei der Ausgabe der Antworten.`,
        resultFormat: {
          university: `Needs to be EXACTLY one of: ${Object.entries(IcsrmUniEnum)
            .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
            .map(([key, value]) => value)
            .join(',')}`,
          department: `Needs to be EXACTLY one of: ${Object.entries(PreferredStudyField)
            .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
            .map(([key, value]) => value)
            .join(',')}`,
          studyField: '',
          academicTitle: '',
          languageSkillsDE: `Needs to be EXACTLY one of: ${Object.entries(SpeechLevel)
            .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
            .map(([key, value]) => value)
            .join(',')}`,
          languageSkillsEN: `Needs to be EXACTLY one of: ${Object.entries(SpeechLevel)
            .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
            .map(([key, value]) => value)
            .join(',')}`,
          expectedCompletionDate: 'needs to be in date format dd/mm/yyyy',
          otherLanguages: [
            {
              language: '',
              level: `Needs to be EXACTLY one of: ${Object.entries(SpeechLevel)
                .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
                .map(([key, value]) => value)
                .join(',')}`,
            },
          ],
          techSkills: '',
          softSkills: [
            `Needs to be EXACTLY one of: ${Object.entries(SoftSkills)
              .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
              .map(([key, value]) => value)
              .join(',')}`,
          ],
          practicalExperiences: [
            {
              description: '',
              level: `Needs to be EXACTLY one of: ${Object.entries(PracticalExperienceLevel)
                .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
                .map(([key, value]) => value)
                .join(',')}`,
            },
          ],
          desiredEmploymentTypes: [
            `Needs to be EXACTLY one of: ${Object.entries(EmploymentType)
              .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
              .map(([key, value]) => value)
              .join(',')}`,
          ],
        },
      });
    } catch (err: any) {
      logger.error({
        message: 'CV extraction failed',
        error: err?.message,
        stack: err?.stack,
        cause: err?.cause?.message,
        fileID: userData.resumeFileId,
        userID: context.userID,
      });
      throw err;
    }

    // const content = {
    //   university: IcsrmUniEnum.ICSRM_GU,
    //   department: PreferredStudyField.COMPUTER_SCIENCE,
    //   studyField: 'Informatik',
    //   academicTitle: 'Bachelor',
    //   languageSkillsDE: SpeechLevel.A2,
    //   languageSkillsEN: SpeechLevel.A2,
    //   otherLanguages: [
    //     {
    //       language: 'Englisch',
    //       level: SpeechLevel.B2,
    //     },
    //   ],
    //   techSkills: 'Java, Python, JavaScript',
    //   softSkills: [SoftSkills.TEAMWORK, SoftSkills.COMMUNICATION, SoftSkills.PROBLEM_SOLVING],
    //   practicalExperiences: [
    //     {
    //       description: 'Praktikum Informatik',
    //       level: PracticalExperienceLevel.EXTENSIVE,
    //     },
    //   ],
    //   desiredEmploymentTypes: [EmploymentType.DIRECT],
    // };

    console.log('Extrahierte Daten: ', JSON.stringify(content, null, 2));
    if (!content) throw new Error('No content extracted');

    const ensureEnumValue = <T>(enumObj: object, value: any, defaultValue: NonNullable<T>): T => {
      return Object.values(enumObj).includes(value) ? (value as T) : defaultValue;
    };

    // validation
    // convert null to undefined to accept incomplete data
    const newUserDataEntityData: PartialEntityData<MWUserData> = {
      university: ensureEnumValue(IcsrmUniEnum, content.university, IcsrmUniEnum.ICSRM_ANDERE),
      department: ensureEnumValue(PreferredStudyField, content.department, PreferredStudyField.OTHER),
      studyField: content.studyField ?? 'unknown',
      academicTitle: Array.isArray(content.academicTitle)
        ? (content.academicTitle.filter(title =>
            Object.values(ExpectedAcademicTitle).includes(title as ExpectedAcademicTitle),
          )[0] as ExpectedAcademicTitle) || ExpectedAcademicTitle.OTHER
        : ensureEnumValue(ExpectedAcademicTitle, content.academicTitle, ExpectedAcademicTitle.OTHER),
      languageSkillsDE: ensureEnumValue(SpeechLevel, content.languageSkillsDE, SpeechLevel.UNKNOWN),
      languageSkillsEN: ensureEnumValue(SpeechLevel, content.languageSkillsEN, SpeechLevel.UNKNOWN),
      otherLanguages: content.otherLanguages.map(
        l =>
          new OtherSpeechLevels({
            _id: A.getGloballyUniqueID(),
            language: l.language,
            level: ensureEnumValue(SpeechLevel, l.level, SpeechLevel.UNKNOWN),
          }),
      ),
      techSkills: content.techSkills ?? 'unknown',
      softSkills: [
        ...new Set(
          (Array.isArray(content.softSkills)
            ? (content.softSkills.filter(skill =>
                Object.values(SoftSkills).includes(skill as SoftSkills),
              ) as SoftSkills[])
            : [SoftSkills.NONE]
          ).concat(SoftSkills.NONE),
        ),
      ]
        .sort((a, b) => {
          const enumValues = Object.values(SoftSkills);
          return enumValues.indexOf(a) - enumValues.indexOf(b);
        })
        .slice(0, MAX_SOFT_SKILLS),
      practicalExperiences: content.practicalExperiences.map(
        l =>
          new PracticalExperience({
            _id: A.getGloballyUniqueID(),
            description: l.description,
            level: ensureEnumValue(PracticalExperienceLevel, l.level, PracticalExperienceLevel.UNKNOWN),
          }),
      ),
      desiredEmploymentTypes: Array.isArray(content.desiredEmploymentTypes)
        ? (content.desiredEmploymentTypes.filter(type =>
            Object.values(EmploymentType).includes(type as EmploymentType),
          ) as EmploymentType[])
        : [],
      expectedCompletionDate: parseDateOnly(content.expectedCompletionDate),
    };

    const isValueIncludedInEnum = (anyEnum: object, value: unknown) =>
      Object.entries(anyEnum)
        .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
        .map(([key, value]) => value)
        .includes(value);

    // Filter out invalid enum values
    if (!isValueIncludedInEnum(IcsrmUniEnum, content.university)) delete newUserDataEntityData.university;
    if (!isValueIncludedInEnum(PreferredStudyField, content.department)) delete newUserDataEntityData.department;
    if (!isValueIncludedInEnum(SpeechLevel, content.languageSkillsDE)) delete newUserDataEntityData.languageSkillsDE;
    if (!isValueIncludedInEnum(SpeechLevel, content.languageSkillsEN)) delete newUserDataEntityData.languageSkillsEN;

    // Filter arrays to only include valid enum values
    newUserDataEntityData.otherLanguages = newUserDataEntityData.otherLanguages?.filter(l =>
      isValueIncludedInEnum(SpeechLevel, l.level),
    );
    newUserDataEntityData.practicalExperiences = newUserDataEntityData.practicalExperiences?.filter(l =>
      isValueIncludedInEnum(PracticalExperienceLevel, l.level),
    );
    // softSkills and desiredEmploymentTypes are already filtered during assignment

    const newUserData = new MWUserData({ _id: userData._id, userID: userData.userID, ...newUserDataEntityData });
    await newUserData.validate({ validateOmitted: false });

    const collection = await scopedCollection(MWUserData);
    await collection.updateOne({ _id: userData._id }, { $set: { ...newUserData.toObject(), resumeFileId: fileID } });
  },
  params: [{ name: 'fileID', type: () => String }],
});
